package us.laantech.common.dto

import com.ionspin.kotlin.bignum.decimal.BigDecimal
import com.ionspin.kotlin.bignum.decimal.RoundingMode
import com.ionspin.kotlin.bignum.decimal.toBigDecimal
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import us.laantech.common.util.generateUUIDv7


enum class CartItemType {
    RETAIL,
    AMOUNT, // An amount of money that is not related to a product
    CASH,
    CARD, // Credit, Debit, or EBT

    RECONCILIATION, // Change due / transaction fees

    FEE,

    //
    PAYOUT,
    EXPENSE
}

/**
 * Lines sharing a key print as a single row, so they must agree on everything the
 * row shows. The per-unit price is part of the key: merging a discounted line into
 * a full price one would print a qty x price product that never matches the total.
 *
 * A null key means "never merge" — no retail product to key on, or a voided line
 * that has to stay visible on its own.
 */
fun CartItem.receiptMergeKey(): Pair<String, Long>? =
    retailProductDTO?.uuid
        ?.takeIf { itemType == CartItemType.RETAIL && !voided }
        ?.let { it to priceCents } // Prevent merge if the price is different

/**
 * True when this line prints on the same row as [row]. A null merge key never
 * merges, so such a line matches only itself.
 */
fun CartItem.mergesWith(row: CartItem): Boolean {
    val key = row.receiptMergeKey() ?: return id != null && id == row.id
    return receiptMergeKey() == key
}

fun List<CartItem>.mergeCartItemsByUUID(): List<CartItem> =
    this.groupBy { it.receiptMergeKey() }
        .flatMap { (key, lines) ->
            if (key == null) lines
            else listOf(lines.first().copy(quantity = lines.sumOf { it.quantity }))
        }

/**
 * The refundable rows of a receipt: the lines that print as one row, kept together.
 *
 * Grouped on [receiptMergeKey], so one product at two prices stays two rows. Keying the
 * result by the RetailProductDTO instead collided them — a per-line discount changes the
 * price without changing the product, so both rows carried an equal DTO and one was lost.
 *
 * Null-key lines — voided, or not a retail product — are dropped: nothing to refund.
 */
fun List<CartItem>.groupByReceiptRow(): List<List<CartItem>> =
    groupBy { it.receiptMergeKey() }
        .mapNotNull { (key, lines) -> if (key == null) null else lines }

@Serializable
data class CartItem(
    val id: String?,
    val itemType: CartItemType = CartItemType.RETAIL,

    val quantity: Double = 1.0, // TODO migrate to integer of 5 decimal places
    val voided: Boolean = false,
    val overridePrice: Long? = null,

    val adjustedPrice: Long? = null,
    val discountAmount: Long? = null,
    val discountPercentNumerator: Double? = null,

    val retailProductDTO: RetailProductDTO? = null,
    val payoutInfo: PayoutInfoDTO? = null,

    ) {
    @Transient
    val uuid: String = retailProductDTO?.uuid
        ?: generateUUIDv7()

    @Transient
    val brand: String = retailProductDTO?.brand
        ?: "Generic"

    val itemName: String = retailProductDTO?.title
        ?: payoutInfo?.payoutType?.name
        ?: "Unnamed Item"

    val upc = retailProductDTO?.barcode

    // Derived from the discount fields, so it must never round-trip through JSON:
    // a persisted value decodes back in and silently wins over this initializer.
    val priceCents: Long get() = retailProductDTO?.priceCents?.let { adjustPrice(it) }
        ?: payoutInfo?.amountCents?.let { adjustPrice(it) }
        ?: adjustPrice(overridePrice ?: 0)

//TODO    val subTotalCents: Long = priceCents * quantity

    @Transient
    val taxable: Boolean = retailProductDTO?.taxable ?: true

    @Transient
    val ebt: Boolean = retailProductDTO?.ebt ?: false

    val imageURL: String? get() = retailProductDTO?.imageUrl ?: retailProductDTO?.imageUrls?.firstOrNull()

    @Transient
    val imageURLs: List<String> = retailProductDTO?.imageUrls ?: emptyList()

    private fun adjustPrice(valueCents: Long): Long {
        // TODO math context and precision stuff

        val priceRef = adjustedPrice?.toBigDecimal() ?: valueCents.toBigDecimal()

        return priceRef
            // Apply percentage discount
            .minus(
                priceRef.times(
                    discountPercentNumerator?.toBigDecimal()?.divide(100.toBigDecimal()) ?: BigDecimal.ZERO
                )
            )
            // Discount by an amount
            .minus(discountAmount?.toBigDecimal() ?: BigDecimal.ZERO)
            .roundToDigitPositionAfterDecimalPoint(2, RoundingMode.ROUND_HALF_AWAY_FROM_ZERO)
            .longValue(exactRequired = false)
    }

    fun isProduct(): Boolean = (this.itemType == CartItemType.RETAIL) || (this.itemType == CartItemType.AMOUNT)


}



